import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import { ItemWithLabel } from '../../../components';
import extractValue, {
  type EventOrValue,
} from '../../../store/utils/extractValue';
import Validator, { ValidatorErrors } from '../../../store/utils/Validator';
import { formatDate } from '../../../utils/date';
import Product from '../../products/store/Product';
import type Sellable from '../../products/store/sellable/Sellable';
import type Promotions from './Promotions';

const DAY = 24 * 60 * 60 * 1000;
const MONTH = 30 * DAY;
const NULL_SELLABLE = new Product({ price: 0 });

export default class Promotion {
  applyForPulseStore = false;
  createdAt = new Date();
  endDate: Date = new Date(Date.now() + MONTH);
  id = Date.now().toString();
  label = '';
  price = 0;
  sellableId = '';
  startDate = new Date();
  updatedAt = new Date();

  isEditing = false;
  isSelected = false;

  saveAction: PromotionSaveAction = 'update';

  static Schema = z
    .object({
      endDate: z.coerce.date(),
      label: z.string().default(''),
      price: z.coerce.number().gt(0),
      sellableId: z.string().min(1, 'select a product'),
      startDate: z.coerce.date(),
    })
    .superRefine((data, ctx) => {
      if (data.endDate < data.startDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['endDate'],
          message: 'should be greater than start date',
        });
      }
    });

  private readonly validator = new Validator(this, Promotion.Schema);

  constructor(
    dto: PromotionConstructorDto,
    private readonly promotions: Promotions,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.update(dto);

    this.validator.validate();
  }

  get allErrorsAsStrings(): string[] {
    return this.validator.allErrorsAsStrings;
  }

  get dateState(): PromotionDateState {
    const now = new Date();

    if (this.endDate < now) {
      return 'expired';
    }

    if (this.startDate > now) {
      return 'future';
    }

    return 'current';
  }

  get displayDiscount(): string {
    const discount = (this.sellable.price - this.price).toFixed(2);
    return `${this.sellable.displayCurrency}${discount}`;
  }

  get displayEndDate(): string {
    return formatDate(this.endDate);
  }

  get displayPrice(): string {
    return this.sellable.displayPrice;
  }

  get displaySalePrice(): string {
    return `${this.sellable.displayCurrency}${this.price}`;
  }

  get displayStartDate(): string {
    return formatDate(this.startDate);
  }

  get errors(): ValidatorErrors<PromotionDto> {
    return this.validator.errorsForEnabledFields;
  }

  get hasErrors(): boolean {
    return !this.validator.isValid;
  }

  get isNew(): boolean {
    return this.saveAction === 'create';
  }

  get sellable(): Sellable {
    const sellable = this.promotions.getSellableById(this.sellableId);
    return sellable || NULL_SELLABLE;
  }

  get sellableName() {
    return this.sellable.name;
  }

  get sellableOptions(): ItemWithLabel[] {
    return this.promotions.sellableOptions;
  }

  cancelEditing() {
    this.setEditing(false);

    if (this.saveAction === 'create') {
      this.promotions.cancelCreating(this);
    }
  }

  async delete() {
    await this.promotions.deletePromotion(this);
  }

  handleChange(event: EventOrValue<keyof Promotion>) {
    const { name, value } = extractValue<keyof Promotion>(event);
    Object.assign(this, { [name]: value });
    this.validator.validate();
    this.validator.enableFieldValidation(name);
  }

  async save() {
    return this.promotions.savePromotion(this);
  }

  setEditing(enabled: boolean) {
    this.isEditing = enabled;
  }

  setSavingAction(action: PromotionSaveAction) {
    this.saveAction = action;
  }

  toJSON(): PromotionDto {
    return {
      applyForPulseStore: this.applyForPulseStore,
      endDate: this.endDate?.toISOString?.() || undefined,
      id: this.saveAction === 'create' ? undefined : this.id,
      label: this.label,
      price: this.price,
      sellableId: this.sellableId,
      startDate: this.startDate?.toISOString?.() || undefined,
    };
  }

  update(dto: PromotionDto) {
    if (!dto) {
      return;
    }

    Object.assign(this, dto);

    this.createdAt = dto.createdAt ? new Date(dto.createdAt) : this.createdAt;
    this.updatedAt = dto.updatedAt ? new Date(dto.updatedAt) : this.updatedAt;
    this.startDate = dto.startDate ? new Date(dto.startDate) : this.startDate;
    this.endDate = dto.endDate ? new Date(dto.endDate) : this.endDate;
  }
}

export type PromotionSaveAction = 'create' | 'delete' | 'update';

export interface PromotionDto {
  applyForPulseStore?: boolean;
  createdAt?: string;
  endDate?: string;
  id?: string;
  label?: string;
  newSellableIds?: string[];
  price?: number;
  sellableId?: string;
  startDate?: string;
  updatedAt?: string;
}

export interface PromotionConstructorDto extends PromotionDto {
  isEditing?: boolean;
  saveAction?: PromotionSaveAction;
}

export type PromotionDateState = 'current' | 'expired' | 'future';
